import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, TextField, Button,  
    CircularProgress, Typography, FormControlLabel, Checkbox, MenuItem,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const CONTRIBUTION_TYPES = [
  { value: "SI", label: "Social Insurance" },
  { value: "HI", label: "Health Insurance" },
  { value: "UI", label: "Unemployment Insurance" },
  { value: "TU", label: "Trade Union" },
  { value: "OT", label: "Other" },
];

const ContributionTypePage = () => {
    useAuthCheck();
    const { contributionTypeId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    
    const [formValues, setFormValues] = useState({
        contributionCode: '',
        contributionType: '',
        description: '',
        isActive: true,
    }); 
    
    useEffect(() => {     
        const fetchContributionType = async () => {
            try {
                setLoading(true);
                
                if (contributionTypeId) {
                    const response = await api.get(`/contributiontypes/${contributionTypeId}`);

                    const formData = response?.data || {};
                    setFormValues({
                        contributionCode: formData.contributionCode || '',
                        contributionType: formData.contributionType || '',
                        description: formData.description || '',
                        isActive: formData.isActive ?? true,
                    })
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        }; 
        
        fetchContributionType();
    }, [contributionTypeId]);
    
    const checkoutSchema = yup.object().shape({
        contributionCode: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const url = contributionTypeId
                ? `/contributiontypes/update/${contributionTypeId}` 
                : '/contributiontypes/new'

            const method = contributionTypeId ? "PUT" : "POST";

            await api({
                method,
                url,
                data: values,
            });
            
            toast.success(contributionTypeId 
                ? 'Contribution type updated successfully.' 
                : 'Contribution type created successfully.'
            );

            if (!contributionTypeId) {
                onSubmitProps.resetForm();
            }

            navigate("/contributiontypes"); 
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to save contribution type!");
            setError(error);
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Contribution Type"
                    subtitle={`${loading ? 'Loading...' : contributionTypeId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/contributiontypes")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Contribution Code"
                                        name="contributionCode"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.contributionCode}
                                        error={!!touched.contributionCode && !!errors.contributionCode}
                                        helperText={touched.contributionCode && errors.contributionCode}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        label="Contribution Type"
                                        name="contributionType"
                                        select
                                        value={values.contributionType || ""}
                                        onChange={(e) =>
                                            setFieldValue(`contributionType`, e.target.value)
                                        }
                                        sx={{ gridColumn: "span 6" }}
                                    >
                                        {CONTRIBUTION_TYPES.map((t) => (
                                            <MenuItem key={t.value} value={t.value}>
                                                {t.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={2}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 12' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {contributionTypeId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default ContributionTypePage;
